import React, { useRef } from "react";
import { Download } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export default function InvoiceTab() {
  const invoiceRef = useRef();

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  const order = {
    date: "2025/10/06 02:07:59",
    reference: "INV-70d4d7d0",
    customer: {
      name: "Dollie Bullock",
      address: "23 Queen Street, Auckland CBD, Auckland 1010, New Zealand",
      phone: "+64 21 555 0175",
      email: "dollie.bullock@lokalai.nz",
    },
    products: [
      {
        id: 1,
        name: "A Walk Amongst Friends - Canvas Print",
        price: 10.24,
        quantity: 1,
      },
      {
        id: 2,
        name: "Lago di Braies - Canvas Print",
        price: 24.62,
        quantity: 1,
      },
      {
        id: 3,
        name: "Never Stop Changing - Canvas Print",
        price: 49.29,
        quantity: 1,
      },
    ],
    subtotal: 39.97,
    tax: 7.44,
    discount: -10.17,
    total: 73.31,
  };

  const handleDownloadPDF = async () => {
    const element = invoiceRef.current;
    const canvas = await html2canvas(element, { scale: 2, useCORS: true });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const margin = 10; // mm
    const imgWidth = pdfWidth - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width + margin;

    let heightLeft = imgHeight;
    let position = margin;

    // 🧾 First page
    pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight - margin * 2;

    // ➕ Additional pages if needed
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + 2 * margin;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight - margin * 2;
    }

    pdf.save(`Invoice_${order.reference}.pdf`);
  };

  return (
    <div className="w-full max-w-5xl bg-white border rounded-lg shadow-sm p-4 lg:p-8 text-gray-700">
      {/* 🔽 PDF Export Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={handleDownloadPDF}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800 transition-all"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </button>
      </div>

      {/* 📄 Invoice Content */}
      <div ref={invoiceRef} className="print:p-0">
        <div className="text-sm text-gray-500 mb-6">{order.date}</div>

        <div className="flex flex-col md:flex-row justify-between md:items-start gap-8 border-b pb-8">
          {/* Left: Invoice + customer info */}
          <div>
            <h2 className="text-lg font-semibold mb-1">{order.reference}</h2>
            <div className="text-gray-700 mt-4 space-y-1 text-sm">
              <p className="font-medium">{order.customer.name}</p>
              <p>{order.customer.address}</p>
              <p>{order.customer.phone}</p>
              <p>{order.customer.email}</p>
            </div>
          </div>

          {/* Right: company box */}
          <div className="bg-dry text-text p-5 rounded-md md:w-[400px]">
            <div className="flex items-start gap-3">
              <img
                src="/images/lokalai_icon.png"
                alt="Lokalai Logo"
                className="w-10 h-10 object-contain "
              />
              <div>
                <p className="font-semibold text-lg">Lokalai Ltd</p>
                <p className="text-sm">
                  123 Manchester Street, Christchurch Central, Christchurch
                  8011, New Zealand
                </p>
                <p className="text-sm">+64 21 123 4567</p>
                <p className="text-sm">info@lokalai.com</p>
                <p className="text-sm">www.lokalai.com</p>
              </div>
            </div>
          </div>
        </div>

        {/* Product Table */}
        <div className="mt-10 overflow-x-auto">
          <table className="min-w-full text-sm text-left border-t border-b">
            <thead className="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
              <tr>
                <th className="p-3">Product</th>
                <th className="p-3 text-right">Price</th>
                <th className="p-3 text-right">Quantity</th>
                <th className="p-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.products.map((p) => (
                <tr key={p.id} className="border-t hover:bg-gray-50">
                  <td className="p-3">{p.name}</td>
                  <td className="p-3 text-right">
                    {formatter.format(p.price)}
                  </td>
                  <td className="p-3 text-right">{p.quantity}</td>
                  <td className="p-3 text-right">
                    {formatter.format(p.price * p.quantity)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end mt-8">
          <table className="text-sm text-right min-w-[250px]">
            <tbody>
              <tr>
                <td className="p-2 text-gray-600">Subtotal</td>
                <td className="p-2">{formatter.format(order.subtotal)}</td>
              </tr>
              <tr>
                <td className="p-2 text-gray-600">Tax</td>
                <td className="p-2">{formatter.format(order.tax)}</td>
              </tr>
              <tr>
                <td className="p-2 text-gray-600">Discount</td>
                <td className="p-2">{formatter.format(order.discount)}</td>
              </tr>
              <tr className="border-t text-lg font-semibold">
                <td className="p-2">Total</td>
                <td className="p-2">{formatter.format(order.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-12 border-t pt-6 text-sm text-gray-600">
          <p className="mb-4 font-medium">
            Please pay within 15 days. Thank you for your business.
          </p>

          <div className="flex items-start gap-3 text-xs text-gray-500 leading-relaxed">
            <img
              src="/images/lokalai_icon.png"
              alt="logo small"
              className="w-5 h-5 object-contain mt-1"
            />
            <p className="text-[11px]">
              Lokalai connects local businesses and customers across New
              Zealand, Australia and Canada with smart booking, ordering, and
              loyalty solutions — helping communities thrive together. Proudly
              based in Christchurch.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// import React from "react";

// export default function InvoiceTab() {
//   const formatter = new Intl.NumberFormat("en-US", {
//     style: "currency",
//     currency: "USD",
//   });

//   const order = {
//     date: "2015/04/25 02:07:59",
//     reference: "70d4d7d0",
//     customer: {
//       name: "Dollie Bullock",
//       address: "23 Queen Street, Auckland CBD, Auckland 1010, New Zealand",
//       phone: "+64 21 555 0175",
//       email: "abbott@withinpixels.com",
//     },
//     products: [
//       {
//         id: 1,
//         name: "A Walk Amongst Friends - Canvas Print",
//         price: 10.24,
//         quantity: 1,
//       },
//       {
//         id: 2,
//         name: "Lago di Braies - Canvas Print",
//         price: 24.62,
//         quantity: 1,
//       },
//       {
//         id: 3,
//         name: "Never Stop Changing - Canvas Print",
//         price: 49.29,
//         quantity: 1,
//       },
//     ],
//     subtotal: 39.97,
//     tax: 77.44,
//     discount: -10.17,
//     total: 73.31,
//   };

//   return (
//     <div className="w-full max-w-5xl  bg-white border rounded-lg shadow-sm p-8 print:p-0 text-gray-700">
//       {/* Header */}
//       <div className="text-sm text-gray-500 mb-6">{order.date}</div>

//       <div className="flex flex-col md:flex-row justify-between md:items-start gap-8 border-b pb-8">
//         {/* Left: Invoice + customer info */}
//         <div>
//           <h2 className="text-lg font-semibold mb-1">
//             INVOICE {order.reference}
//           </h2>

//           <div className="text-gray-700 mt-4 space-y-1 text-sm">
//             <p className="font-medium">{order.customer.name}</p>
//             <p>{order.customer.address}</p>
//             <p>{order.customer.phone}</p>
//             <p>{order.customer.email}</p>
//           </div>
//         </div>

//         {/* Right: company box */}
//         <div className="bg-dry text-text p-6 rounded-md md:w-[400px]">
//           <div className="flex items-start gap-3">
//             <img
//               src="/images/lokalai_icon.png"
//               alt="Fuse Logo"
//               className="w-10 h-10 object-contain"
//             />
//             <div>
//               <p className="font-semibold text-lg">Lokalai Ltd</p>
//               <p className="text-sm">
//                 123 Manchester Street, Christchurch Central, Christchurch 8011,
//                 New Zealand
//               </p>
//               <p className="text-sm">+64 21 123 4567</p>
//               <p className="text-sm">info@lokalai.com</p>
//               <p className="text-sm">www.lokalai.com</p>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Product Table */}
//       <div className="mt-10 overflow-x-auto">
//         <table className="min-w-full text-sm text-left border-t border-b">
//           <thead className="bg-gray-100 text-gray-600 uppercase text-xs font-semibold">
//             <tr>
//               <th className="p-3">Product</th>
//               <th className="p-3 text-right">Price</th>
//               <th className="p-3 text-right">Quantity</th>
//               <th className="p-3 text-right">Total</th>
//             </tr>
//           </thead>
//           <tbody>
//             {order.products.map((p) => (
//               <tr key={p.id} className="border-t hover:bg-gray-50">
//                 <td className="p-3">{p.name}</td>
//                 <td className="p-3 text-right">{formatter.format(p.price)}</td>
//                 <td className="p-3 text-right">{p.quantity}</td>
//                 <td className="p-3 text-right">
//                   {formatter.format(p.price * p.quantity)}
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* Totals */}
//       <div className="flex justify-end mt-8">
//         <table className="text-sm text-right min-w-[250px]">
//           <tbody>
//             <tr>
//               <td className="p-2 text-gray-600">Subtotal</td>
//               <td className="p-2">{formatter.format(order.subtotal)}</td>
//             </tr>
//             <tr>
//               <td className="p-2 text-gray-600">Tax</td>
//               <td className="p-2">{formatter.format(order.tax)}</td>
//             </tr>
//             <tr>
//               <td className="p-2 text-gray-600">Discount</td>
//               <td className="p-2">{formatter.format(order.discount)}</td>
//             </tr>
//             <tr className="border-t text-lg font-semibold">
//               <td className="p-2">Total</td>
//               <td className="p-2">{formatter.format(order.total)}</td>
//             </tr>
//           </tbody>
//         </table>
//       </div>

//       {/* Footer */}
//       <div className="mt-12 border-t pt-6 text-sm text-gray-600">
//         <p className="mb-4 font-medium">
//           Please pay within 15 days. Thank you for your business.
//         </p>

//         <div className="flex items-start gap-3 text-xs text-gray-500 leading-relaxed">
//           <img
//             src="/images/lokalai_icon.png"
//             alt="logo small"
//             className="w-5 h-5 object-contain grayscale mt-1"
//           />
//           <p className="text-[11px]">
//             Lokalai connects local businesses and customers across New Zealand,
//             Australia and Canada with smart booking, ordering, and loyalty
//             solutions — helping communities thrive together. Proudly based in
//             Christchurch.
//           </p>
//         </div>
//       </div>
//     </div>
//   );
// }
